// src/components/PasswordEye.jsx
// Animated robot toggle for the password field. Visor lifts when the password
// is visible and the LED eyes track the cursor.

import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

export default function PasswordEye({ visible, onToggle }) {
  const ref = useRef(null);
  const [pupil, setPupil] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);

  // LED eyes track the cursor while the visor is up
  useEffect(() => {
    if (!visible) { setPupil({ x: 0, y: 0 }); return; }
    const onMove = (e) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const angle = Math.atan2(e.clientY - cy, e.clientX - cx);
      const dist = 1.4;
      setPupil({ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [visible]);

  const spring = { type: "spring", stiffness: 320, damping: 18 };

  // pre-compute animated positions so they are never undefined
  const lx = 11.5 + pupil.x;
  const ly = 15 + pupil.y;
  const rx = 20.5 + pupil.x;
  const ry = 15 + pupil.y;

  return (
    <button
      type="button"
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={ref}
      className="absolute right-2.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg
        hover:bg-tb-brand/5 transition-colors"
      aria-label={visible ? "Hide password" : "Show password"}
    >
      <motion.svg
        width="26" height="26" viewBox="0 0 32 32"
        animate={{ scale: hovered ? 1.1 : 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 15 }}
      >
        {/* antenna */}
        <line x1="16" y1="7" x2="16" y2="3.5"
          stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
          className="text-tb-muted" />
        <motion.circle
          cx={16} cy={3} r={1.6} fill="#00ACC1"
          initial={{ cy: 3, opacity: 1 }}
          animate={{ cy: hovered ? 2.2 : 3, opacity: [1, 0.5, 1] }}
          transition={{ cy: spring, opacity: { repeat: Infinity, duration: 1.4 } }}
        />

        {/* head */}
        <rect x="5" y="7" width="22" height="19" rx="6" fill="none"
          stroke="currentColor" strokeWidth="1.4" className="text-tb-muted" />

        {/* side bolts */}
        <circle cx="4.5" cy="16" r="1.3" className="fill-tb-muted" />
        <circle cx="27.5" cy="16" r="1.3" className="fill-tb-muted" />

        {/* LED eyes */}
        <motion.circle cx={11.5} cy={15} r={2} fill="#00ACC1"
          initial={{ cx: 11.5, cy: 15 }}
          animate={{ cx: lx, cy: ly }} transition={spring} />
        <motion.circle cx={20.5} cy={15} r={2} fill="#00ACC1"
          initial={{ cx: 20.5, cy: 15 }}
          animate={{ cx: rx, cy: ry }} transition={spring} />
        {/* led glints */}
        <motion.circle cx={12} cy={14.5} r={0.6} fill="white"
          initial={{ cx: 12, cy: 14.5 }}
          animate={{ cx: 12 + pupil.x, cy: 14.5 + pupil.y }} transition={spring} />
        <motion.circle cx={21} cy={14.5} r={0.6} fill="white"
          initial={{ cx: 21, cy: 14.5 }}
          animate={{ cx: 21 + pupil.x, cy: 14.5 + pupil.y }} transition={spring} />

        {/* mouth grille */}
        <g stroke="currentColor" strokeWidth="1" strokeLinecap="round" className="text-tb-muted">
          <line x1="12" y1="21.5" x2="12" y2="23" />
          <line x1="14.5" y1="21.5" x2="14.5" y2="23" />
          <line x1="17" y1="21.5" x2="17" y2="23" />
          <line x1="19.5" y1="21.5" x2="19.5" y2="23" />
        </g>

        {/* sliding visor */}
        <motion.g
          initial={{ y: 0 }}
          animate={{ y: visible ? -8.5 : 0 }}
          transition={spring}
        >
          <rect x="6.5" y="11.5" width="19" height="7" rx="3" fill="#00838f" />
          <rect x="6.5" y="11.5" width="19" height="2.4" rx="1.2" fill="#00ACC1" opacity="0.7" />
        </motion.g>
      </motion.svg>
    </button>
  );
}